import {OperationLocationT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import StorageHelper from '@freckle/educator-entities/ts/common/helpers/storage-helper'

// High-level key identifying a class of operation
// E.g. 'merge-duplicate-students'. Should never appear to users.
type OperationKey = string

// What we store (in local storage) so that we can remember what operations are
// running across sessions/refresh/etc.
type OperationStore = Record<OperationKey, OperationLocationT>

const OPERATION_STORE_KEY = 'freckle-operation-store'

type OperationPersistenceResult = {
  //  Returns the operation location for any operations that the given key
  // null if the operation is not currently pending
  pendingLocationFor: (key: OperationKey) => OperationLocationT | null

  // Track new pending operation
  track: (key: OperationKey, operation: OperationLocationT) => void

  // Remove a currently tracked operation by location
  remove: (key: OperationKey) => void

  // Totally wipe the operation store (for testing purposes)
  clear: () => void
}

export const useOperationPersistence = (): OperationPersistenceResult => {
  const getStore = () =>
    StorageHelper.getLocalStoragePropertyDefault<OperationStore>(OPERATION_STORE_KEY, {})

  const mutateStore = (mutation: (store: OperationStore) => OperationStore): void => {
    const store = getStore()
    const newStore = mutation(store)
    StorageHelper.setLocalStorageProperty(OPERATION_STORE_KEY, newStore)
  }

  return {
    remove: (key: OperationKey) =>
      mutateStore(store => {
        delete store[key]
        return store
      }),
    pendingLocationFor: (key: OperationKey) => {
      const store = getStore()
      return store[key] ?? null
    },
    track: (key: OperationKey, location: OperationLocationT) =>
      mutateStore(store => {
        store[key] = location
        return store
      }),
    clear: () => StorageHelper.clearLocalStorageProperty(OPERATION_STORE_KEY),
  }
}
